import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, X } from 'lucide-react';
import { de } from '../lib/i18n/de';
import { useAuthOrSession } from '../lib/authOrSession';
import { getCase, type CaseRow } from '../lib/supabaseCases';
import { Button } from '../components/ui/Button';
import { decideReview, getOpenReviewForCase } from '../lib/supabaseCaseReviews';
import type { CaseReviewRow } from '../lib/supabaseTypes';
import { useWizardStore } from '../lib/store/wizard';
import { useWizardSync } from '../lib/store/useWizardSync';
import { selectAssessmentsForCase } from '../lib/statement/assessmentsAdapter';
import { ErrorPanel } from '../components/ui/ErrorPanel';

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; caseRow: CaseRow; review: CaseReviewRow }
  | { kind: 'no-review' }
  | { kind: 'error'; message: string };

type DecisionState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'done'; decision: 'approve' | 'reject' }
  | { kind: 'error'; message: string };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Review-Ansicht für die zweite Bewertung im Vier-Augen-Prinzip (AP-B7).
 *
 * Zeigt Stammdaten + Bewertungstexte read-only und bietet Approve/Reject-
 * Buttons mit Pflicht-Kommentar.
 *
 * Zugriffsschutz:
 *  - URL-Validation (UUID)
 *  - RLS: nur Berater desselben Tenants sehen den offenen Review-Eintrag
 *  - Trigger im DB-Layer: Reviewer ≠ Owner ≠ requested_by
 */
export function CaseReview() {
  const navigate = useNavigate();
  const { id: caseId = '' } = useParams<{ id?: string }>();
  const { user } = useAuthOrSession();
  const [load, setLoad] = useState<LoadState>({ kind: 'idle' });
  const [comment, setComment] = useState('');
  const [decision, setDecision] = useState<DecisionState>({ kind: 'idle' });
  const draft = useWizardStore((s) => s.cases[caseId]?.assessments);

  // Vier-Augen-Prinzip: Der Reviewer muss die fachlichen Bewertungstexte des
  // Owners sehen können, um überhaupt fundiert freigeben zu dürfen. Diese liegen
  // serverseitig (dimension_data.structured_data.assessments, Berater-Ebene) —
  // wir hydrieren sie über useWizardSync. Der Reviewer verändert nichts, der
  // Saver löst also nichts aus (er reagiert nur auf lokale Mutationen).
  const reviewSync = useWizardSync(caseId, {
    userId: UUID_RE.test(caseId) ? (user?.id ?? null) : null,
    layer: 'berater',
  });
  const assessmentsLoaded = selectAssessmentsForCaseSafe(draft) !== null;
  const assessmentsLoading = reviewSync.status === 'loading' || reviewSync.status === 'idle';

  useEffect(() => {
    if (!UUID_RE.test(caseId)) {
      setLoad({ kind: 'error', message: 'Ungültige Mandats-ID.' });
      return;
    }
    setLoad({ kind: 'loading' });
    void Promise.all([getCase(caseId), getOpenReviewForCase(caseId)])
      .then(([caseRow, review]) => {
        if (!caseRow) {
          setLoad({ kind: 'error', message: 'Mandat nicht gefunden oder keine Berechtigung.' });
          return;
        }
        if (!review) {
          setLoad({ kind: 'no-review' });
          return;
        }
        setLoad({ kind: 'ready', caseRow, review });
      })
      .catch((err: unknown) => {
        setLoad({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Fehler beim Laden.',
        });
      });
  }, [caseId]);

  async function handleDecide(d: 'approve' | 'reject') {
    if (load.kind !== 'ready') return;
    if (d === 'approve' && !assessmentsLoaded) {
      // Vier-Augen-Prinzip: ohne einsehbare Bewertungstexte keine Freigabe.
      setDecision({
        kind: 'error',
        message:
          'Eine Freigabe ist erst möglich, wenn die fachlichen Bewertungstexte geladen und vollständig sind.',
      });
      return;
    }
    if (d === 'reject' && comment.trim().length === 0) {
      setDecision({
        kind: 'error',
        message: 'Bei einer Ablehnung ist ein Kommentar erforderlich.',
      });
      return;
    }
    setDecision({ kind: 'submitting' });
    try {
      const trimmed = comment.trim();
      await decideReview(load.review.id, load.caseRow.id, {
        decision: d,
        ...(trimmed.length > 0 ? { comment: trimmed } : {}),
      });
      setDecision({ kind: 'done', decision: d });
    } catch (err) {
      setDecision({
        kind: 'error',
        message:
          err instanceof Error ? err.message : 'Entscheidung konnte nicht gespeichert werden.',
      });
    }
  }

  return (
    <div className="pb-24 pt-12 md:pt-20">
      <button
        type="button"
        onClick={() => {
          void navigate('/cases');
        }}
        className="mb-6 inline-flex items-center gap-1.5 font-sans text-xs font-medium uppercase tracking-widest text-stone-500 hover:text-stone-900"
      >
        <ArrowLeft size={14} />
        Mandate
      </button>

      <header className="relative pb-10">
        <div
          aria-hidden="true"
          className="absolute right-0 top-0 hidden h-24 w-24 rounded-sm md:block"
          style={{ backgroundColor: 'var(--color-dim-style)', opacity: 0.32 }}
        />
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          Vier-Augen-Prinzip
        </p>
        <h1 className="mt-3 font-serif text-4xl font-black leading-none tracking-tight text-stone-900 md:text-6xl">
          ZWEITE BEWERTUNG.
        </h1>
        <p className="mt-3 max-w-prose font-serif text-base italic text-stone-500 md:text-lg">
          Stammdaten und Bewertungstexte zur kollegialen Prüfung. Eine Stellungnahme darf erst nach
          Freigabe generiert werden.
        </p>
      </header>

      {load.kind === 'loading' && (
        <p className="font-serif text-base italic text-stone-500">Lade Mandat zur Prüfung…</p>
      )}

      {load.kind === 'error' && <ErrorBox message={load.message} />}

      {load.kind === 'no-review' && (
        <section className="rounded-sm border border-stone-200 bg-stone-50 px-6 py-8 md:px-10">
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700">
            Kein offener Review
          </p>
          <p className="mt-2 max-w-prose font-serif text-sm italic text-stone-500">
            Für dieses Mandat liegt aktuell keine Anfrage zur zweiten Bewertung vor. Der Owner muss
            den Review-Workflow im Cockpit erst anstoßen.
          </p>
        </section>
      )}

      {load.kind === 'ready' && (
        <>
          {/* Identifikation */}
          <section className="mb-8 rounded-sm border border-stone-200 bg-white px-6 py-6 md:px-10">
            <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
              Mandat
            </p>
            <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900">
              {load.caseRow.client_full_name ?? '— ohne Mandanten-Name —'}
            </h2>
            <dl className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <Detail label="Aktenzeichen" value={load.caseRow.jc_file_number ?? '—'} />
              <Detail
                label="Auftragsart"
                value={load.caseRow.type === 'b2g' ? 'B2G · Jobcenter-Auftrag' : 'B2C · Mandant'}
              />
              <Detail label="Rechtsgrundlage" value={legalBasisLabel(load.caseRow.legal_basis)} />
              <Detail
                label="Jobcenter"
                value={load.caseRow.jobcenter_name ?? '—'}
                hidden={load.caseRow.type !== 'b2g'}
              />
              <Detail
                label="Anforderung"
                value={new Date(load.review.requested_at).toLocaleString('de-DE')}
              />
            </dl>
          </section>

          {/* Bewertungstexte read-only */}
          <section className="mb-8 rounded-sm border border-stone-200 bg-white px-6 py-6 md:px-10">
            <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
              Bewertungstexte
            </p>
            <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900">
              EINORDNUNG DES OWNERS.
            </h2>

            {assessmentsLoading ? (
              <p className="mt-4 font-serif text-sm italic text-stone-500">
                Bewertungstexte werden geladen…
              </p>
            ) : reviewSync.status === 'error' ? (
              <p className="mt-4 font-serif text-sm italic text-red-700">
                Die Bewertungstexte konnten nicht geladen werden. Bitte laden Sie die Seite neu.
                Eine Freigabe ist ohne Einsicht in die Texte nicht möglich.
              </p>
            ) : assessmentsLoaded ? (
              <AssessmentReadOnly assessments={selectAssessmentsForCaseSafe(draft)} />
            ) : (
              <p className="mt-4 font-serif text-sm italic text-stone-500">
                Für dieses Mandat sind noch keine vollständigen fachlichen Bewertungstexte erfasst.
                Eine Freigabe ist erst möglich, sobald der Owner die Bewertung abgeschlossen hat.
                Sie können den Fall mit einem Kommentar zur Überarbeitung zurückgeben.
              </p>
            )}
          </section>

          {/* Entscheidung */}
          <section className="rounded-sm border border-stone-200 bg-white px-6 py-6 md:px-10">
            <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
              Entscheidung
            </p>
            <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900">
              IHR VOTUM.
            </h2>

            {load.review.requested_by === user?.id && (
              <p className="mt-4 rounded-sm bg-amber-50 px-3 py-2 font-sans text-xs text-amber-900">
                Sie haben diesen Review selbst angefordert und können ihn aus Vier-Augen-Gründen
                nicht selbst entscheiden.
              </p>
            )}

            <label
              htmlFor="review-comment"
              className="mt-6 block font-sans text-xs font-medium uppercase tracking-widest text-stone-700"
            >
              Kommentar
              <span className="ml-2 font-serif text-xs italic text-stone-500 normal-case tracking-normal">
                Bei Ablehnung verpflichtend.
              </span>
            </label>
            <textarea
              id="review-comment"
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
              }}
              rows={4}
              placeholder="Anmerkungen zur Plausibilität der Bewertungstexte, fehlende Belege, formale Beanstandungen …"
              className="mt-1 w-full rounded-sm border border-stone-200 bg-white px-3 py-2 font-serif text-base text-stone-900 focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900"
            />

            {decision.kind === 'error' && (
              <div className="mt-4">
                <ErrorPanel messages={[decision.message]} />
              </div>
            )}

            {decision.kind === 'done' ? (
              <div className="mt-6 rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3">
                <p className="font-sans text-xs font-medium uppercase tracking-widest text-emerald-800">
                  Entscheidung gespeichert
                </p>
                <p className="mt-2 font-serif text-sm text-emerald-900">
                  {decision.decision === 'approve'
                    ? 'Der Case ist auf „abgeschlossen" gesetzt. Der Owner kann die Stellungnahme jetzt im Cockpit generieren.'
                    : 'Der Case bleibt im Status „Review". Der Owner kann den Inhalt überarbeiten und erneut zur Prüfung freigeben.'}
                </p>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    void navigate('/cases');
                  }}
                  className="mt-4"
                >
                  Zur Mandantenliste →
                </Button>
              </div>
            ) : (
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    void handleDecide('approve');
                  }}
                  disabled={
                    decision.kind === 'submitting' ||
                    load.review.requested_by === user?.id ||
                    !assessmentsLoaded
                  }
                  title={
                    !assessmentsLoaded
                      ? 'Freigabe erst möglich, wenn die Bewertungstexte geladen und vollständig sind.'
                      : undefined
                  }
                  className="inline-flex items-center justify-center gap-2 rounded-sm bg-emerald-700 px-6 py-3 font-sans text-xs font-medium uppercase tracking-widest text-white transition-colors hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-stone-300"
                >
                  <Check size={14} />
                  Freigeben
                </button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    void handleDecide('reject');
                  }}
                  disabled={decision.kind === 'submitting' || load.review.requested_by === user?.id}
                >
                  <X size={14} />
                  Zur Überarbeitung zurückgeben
                </Button>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sub-Components
// -----------------------------------------------------------------------------

interface DetailProps {
  label: string;
  value: string;
  hidden?: boolean;
}

function Detail({ label, value, hidden }: DetailProps) {
  if (hidden) return null;
  return (
    <div>
      <dt className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {label}
      </dt>
      <dd className="mt-1 font-serif text-base text-stone-900">{value}</dd>
    </div>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <section className="rounded-sm border border-red-200 bg-red-50 px-6 py-5 md:px-10">
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-red-800">Fehler</p>
      <p className="mt-2 font-serif text-sm text-red-900">{message}</p>
    </section>
  );
}

interface AssessmentReadOnlyProps {
  assessments: ReturnType<typeof selectAssessmentsForCaseSafe>;
}

// Menschenlesbare Überschriften der Bewertungs-Slots (statt roher Objekt-Keys).
const ASSESSMENT_LABELS: Readonly<Record<string, string>> = {
  verdictLongParagraph: 'Gesamteinschätzung',
  verdictReasoning: 'Begründung des Votums',
  recommendationDetailed: 'Empfehlung',
  constraintDiscussion: 'Erörterung der Zumutbarkeit',
  riskIndicators: 'Risikoindikatoren',
  strengthIndicators: 'Stärken und Stabilisatoren',
  dimensionTexts: 'Bewertung der Dimensionen',
  revenueAssessment: 'Umsatz',
  costsAssessment: 'Kosten',
  profitAssessment: 'Gewinn',
  hourlyRateAssessment: 'Stundensatz',
  liquidityAssessment: 'Liquidität',
  runwayAssessment: 'Finanzielle Reichweite',
  utilizationAssessment: 'Auslastung',
  gapInterpretation: 'Tragfähigkeitslücke',
  conditionsParagraph: 'Auflagen',
  reevaluationProposal: 'Vorschlag zur Wiedervorlage',
};

const DIMENSION_NAMES: Readonly<Record<string, string>> = {
  identity: de.dimensions.identity.name,
  activity: de.dimensions.activity.name,
  style: de.dimensions.style.name,
  resources: de.dimensions.resources.name,
  health: de.dimensions.health.name,
  private: de.dimensions.private.name,
};

function AssessmentReadOnly({ assessments }: AssessmentReadOnlyProps) {
  if (!assessments) {
    return (
      <p className="mt-4 font-serif text-sm italic text-stone-500">
        Noch keine vollständigen Bewertungstexte erfasst — der Owner muss den Editor zuerst
        ausfüllen.
      </p>
    );
  }
  const entries = Object.entries(assessments);
  return (
    <dl className="mt-4 space-y-5">
      {entries.map(([key, value]) => (
        <div key={key}>
          <dt className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700">
            {ASSESSMENT_LABELS[key] ?? key}
          </dt>
          {key === 'dimensionTexts' && value && typeof value === 'object' ? (
            <dl className="mt-2 space-y-3 border-l-2 border-stone-100 pl-4">
              {Object.entries(value as Record<string, unknown>).map(([dim, text]) => (
                <div key={dim}>
                  <dt className="font-sans text-[11px] font-medium uppercase tracking-widest text-stone-500">
                    {DIMENSION_NAMES[dim] ?? dim}
                  </dt>
                  <dd className="mt-0.5 whitespace-pre-wrap font-serif text-sm leading-relaxed text-stone-700">
                    {typeof text === 'string' && text.trim().length > 0 ? text : '—'}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <dd className="mt-1 whitespace-pre-wrap font-serif text-sm leading-relaxed text-stone-700">
              {typeof value === 'string' && value.length > 0 ? value : '—'}
            </dd>
          )}
        </div>
      ))}
    </dl>
  );
}

/**
 * Wrapper um selectAssessmentsForCase, der ein optionales Ergebnis liefert.
 * Wenn der Owner-Editor unvollständig ist, gibt selectAssessmentsForCase
 * `null` zurück; bei einem unerwarteten Fehler fangen wir defensiv ab.
 */
function selectAssessmentsForCaseSafe(
  draft: ReturnType<typeof useWizardStore.getState>['cases'][string]['assessments'] | undefined,
): Record<string, unknown> | null {
  if (!draft) return null;
  try {
    const result = selectAssessmentsForCase(draft);
    return (result as unknown as Record<string, unknown>) ?? null;
  } catch {
    return null;
  }
}

function legalBasisLabel(b: string): string {
  switch (b) {
    case 'par10':
      return '§ 10 Abs. 2 Nr. 5 SGB II';
    case 'par16b':
      return '§ 16b SGB II Einstiegsgeld';
    case 'par16c':
      return '§ 16c SGB II Förderung Selbständigkeit';
    case 'par93':
      return '§ 93 SGB III Gründungszuschuss';
    default:
      return b;
  }
}
